import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Plus, Search, Filter, List, Grid, ChevronDown, ChevronRight, Download, FileSpreadsheet, Paperclip } from 'lucide-react';
import { TransactionWithDetails } from '../lib/services/TransactionService';
import TransactionForm from './TransactionForm';
import { useAccounts, useCategories } from '../hooks';
import { downloadTransactionsCSV, downloadTransactionsXLSX } from '../lib/services/ExportService';
import CategoryIconBadge from './CategoryIconBadge';
import { transactionTypeLabel } from '../lib/transactionCashflow';

interface TransactionListProps {
  transactions: TransactionWithDetails[];
  loading: boolean;
  onAddTransaction: (transaction: any) => Promise<{ success: boolean; error?: string }>;
  onEditTransaction: (id: string, updates: any) => Promise<{ success: boolean; error?: string }>;
  onDeleteTransaction: (id: string) => Promise<{ success: boolean; error?: string }>;
  initialSearchTerm?: string;
}

const TransactionList: React.FC<TransactionListProps> = ({
  transactions,
  loading,
  onAddTransaction,
  onEditTransaction,
  onDeleteTransaction,
  initialSearchTerm = ''
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingTransaction, setEditingTransaction] = useState<TransactionWithDetails | null>(null);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [filterType, setFilterType] = useState<
    'all' | 'depense' | 'revenu' | 'transfert' | 'pret' | 'remboursement'
  >('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterCompte, setFilterCompte] = useState<string>('all');
  const [filterMonth, setFilterMonth] = useState<string>('');
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const [showYearPicker, setShowYearPicker] = useState(false);
  type GroupByOption = 'none' | 'month' | 'year' | 'type';
  const [groupBy, setGroupBy] = useState<GroupByOption>('none');
  /** Groupes repliés (clés dans le Set = replié). Vide = tout déplié. */
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const { categories } = useCategories();
  const { comptes } = useAccounts();

  const toggleGroup = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const MONTHS = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];
  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: 8 }, (_, i) => currentYear - i);

  // Valeurs dérivées pour affichage current selection
  const selectedDate = filterMonth ? new Date(filterMonth) : null;
  const selectedMonthIdx = selectedDate ? selectedDate.getMonth() : null;
  const selectedYear = selectedDate ? selectedDate.getFullYear() : null;
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Nombre d'éléments par page
  const [paginationEnabled, setPaginationEnabled] = useState(false); // Par défaut : tout afficher (plus pratique sur un mois)

  // Synchroniser searchTerm avec initialSearchTerm quand il change
  useEffect(() => {
    setSearchTerm(initialSearchTerm);
    setCurrentPage(1); // Remettre à la première page quand on change de recherche
  }, [initialSearchTerm]);

  // Après upload PJ ou refresh liste : aligner la transaction ouverte sur les données fraîches (attachment_path, etc.)
  useEffect(() => {
    if (!isFormOpen) return;
    setEditingTransaction((prev) => {
      if (!prev?.id) return prev;
      const fresh = transactions.find((t) => t.id === prev.id);
      if (!fresh) return prev;
      if (prev.attachment_path === fresh.attachment_path && prev.updated_at === fresh.updated_at) {
        return prev;
      }
      return {
        ...prev,
        attachment_path: fresh.attachment_path,
        updated_at: fresh.updated_at,
      };
    });
  }, [transactions, isFormOpen]);

  const handleEdit = (transaction: TransactionWithDetails) => {
    setEditingTransaction(transaction);
    setIsFormOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette transaction ?')) {
      const result = await onDeleteTransaction(id);
      if (!result.success) {
        alert(`Erreur lors de la suppression : ${result.error}`);
      }
    }
  };

  const handleFormSubmit = async (transaction: any) => {
    let result;
    if (editingTransaction && editingTransaction.id) {
      result = await onEditTransaction(editingTransaction.id, transaction);
    } else {
      result = await onAddTransaction(transaction);
    }

    if (result.success) {
      setIsFormOpen(false);
      setEditingTransaction(null);
    } else {
      alert(`Erreur : ${result.error}`);
    }
    return result;
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setEditingTransaction(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
    }).format(Math.abs(amount));
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  // Filtrer les transactions
  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.categorie?.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         transaction.compte?.nom.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || transaction.type === filterType;
    
    // Filtre par catégorie
    const matchesCategory = filterCategory === 'all' || transaction.categorie_id === filterCategory;

    // Filtre par compte (source ou destination pour les transferts)
    const matchesCompte =
      filterCompte === 'all' ||
      transaction.compte_id === filterCompte ||
      transaction.compte_destination_id === filterCompte;
    
    // Filtre par mois
    let matchesMonth = true;
    if (filterMonth) {
      const transactionDate = new Date(transaction.date);
      const filterDate = new Date(filterMonth);
      matchesMonth = transactionDate.getFullYear() === filterDate.getFullYear() && 
                     transactionDate.getMonth() === filterDate.getMonth();
    }
    
    return matchesSearch && matchesType && matchesCategory && matchesCompte && matchesMonth;
  });

  const hasActiveFilters =
    !!searchTerm ||
    filterType !== 'all' ||
    filterCategory !== 'all' ||
    filterCompte !== 'all' ||
    !!filterMonth;

  // Regroupement des transactions (quand groupBy !== 'none')
  const getGroupKey = (t: TransactionWithDetails): string => {
    const d = new Date(t.date);
    if (groupBy === 'month') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (groupBy === 'year') return String(d.getFullYear());
    if (groupBy === 'type') return t.type || 'autre';
    return '';
  };

  const getGroupLabel = (key: string): string => {
    if (groupBy === 'month') {
      const [y, m] = key.split('-');
      return `${MONTHS[parseInt(m, 10) - 1]} ${y}`;
    }
    if (groupBy === 'year') return key;
    if (groupBy === 'type') {
      const labels: Record<string, string> = {
        depense: 'Dépenses',
        revenu: 'Revenus',
        transfert: 'Transferts',
        pret: 'Prêts',
        remboursement: 'Remboursements',
      };
      return labels[key] || key;
    }
    return key;
  };

  const groupedEntries = (): { key: string; label: string; items: TransactionWithDetails[] }[] => {
    if (groupBy === 'none') return [];
    const map = new Map<string, TransactionWithDetails[]>();
    for (const t of filteredTransactions) {
      const k = getGroupKey(t);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(t);
    }
    const entries = Array.from(map.entries()).map(([key, items]) => ({
      key,
      label: getGroupLabel(key),
      items,
    }));
    if (groupBy === 'month' || groupBy === 'year') {
      entries.sort((a, b) => b.key.localeCompare(a.key));
    }
    if (groupBy === 'type') {
      const order = ['depense', 'revenu', 'transfert', 'pret', 'remboursement'];
      entries.sort((a, b) => order.indexOf(a.key) - order.indexOf(b.key));
    }
    return entries;
  };

  const hasGrouping = groupBy !== 'none';
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = paginationEnabled && !hasGrouping
    ? filteredTransactions.slice(startIndex, endIndex)
    : filteredTransactions;

  // Remettre à la première page si la page actuelle n'existe plus
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Spinner uniquement au premier chargement. En refetch (ex. retour Alt+Tab), garder la liste
  // pour ne pas démonter le formulaire "Nouvelle transaction" et perdre la saisie.
  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto"></div>
          <p className="text-body-sm text-white mt-3">Chargement des transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
       <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
         <div>
           <p className="text-body-sm text-dark-400">
             {filteredTransactions.length} transaction{filteredTransactions.length !== 1 ? 's' : ''}
             {hasActiveFilters && transactions.length !== filteredTransactions.length && (
               <span className="ml-2 text-sm">
                 (sur {transactions.length} total)
               </span>
             )}
             {paginationEnabled && totalPages > 1 && (
               <span className="ml-2 text-sm">
                 • Page {currentPage} sur {totalPages}
               </span>
             )}
             {!paginationEnabled && (
               <span className="ml-2 text-sm text-green-400">
                 • Toutes affichées
               </span>
             )}
           </p>
         </div>

        <div className="flex flex-wrap items-center gap-2 justify-end">
          <button
            type="button"
            onClick={() => downloadTransactionsCSV(filteredTransactions)}
            disabled={filteredTransactions.length === 0}
            title="Exporter toutes les transactions correspondant aux filtres (pas seulement la page courante)"
            className="inline-flex items-center gap-1.5 px-3 py-2 h-10 max-h-10 text-sm font-medium rounded-md border border-dark-600 bg-dark-800 text-dark-200 hover:bg-dark-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-4 h-4" />
            CSV
          </button>
          <button
            type="button"
            onClick={() => downloadTransactionsXLSX(filteredTransactions)}
            disabled={filteredTransactions.length === 0}
            title="Exporter au format Excel (.xlsx), mêmes lignes que le CSV"
            className="inline-flex items-center gap-1.5 px-3 py-2 h-10 max-h-10 text-sm font-medium rounded-md border border-dark-600 bg-dark-800 text-dark-200 hover:bg-dark-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Excel
          </button>
          <button
            type="button"
            onClick={() => setIsFormOpen(true)}
            className="flex items-center space-x-2 px-3 py-2 h-10 max-h-10 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-md transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Nouvelle transaction</span>
          </button>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-dark-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Rechercher une transaction..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 text-sm bg-dark-800 border border-dark-700 rounded-md text-white placeholder-dark-400 focus:outline-none focus:border-primary-500"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as any)}
          className="px-3 py-1.5 text-sm bg-dark-800 border border-dark-700 rounded-md text-white focus:outline-none focus:border-primary-500"
        >
          <option value="all">Tous les types</option>
          <option value="depense">Dépenses</option>
          <option value="revenu">Revenus</option>
          <option value="transfert">Transferts</option>
          <option value="pret">Prêts</option>
          <option value="remboursement">Remboursements</option>
        </select>

        <select
          value={filterCategory}
          onChange={(e) => {
            setFilterCategory(e.target.value);
            setCurrentPage(1);
          }}
          className="px-3 py-1.5 text-sm bg-dark-800 border border-dark-700 rounded-md text-white focus:outline-none focus:border-primary-500"
        >
          <option value="all">Toutes les catégories</option>
          {categories.map((category) => (
            <option key={category.id} value={category.id}>
              {category.nom}
            </option>
          ))}
        </select>

        <select
          value={filterCompte}
          onChange={(e) => {
            setFilterCompte(e.target.value);
            setCurrentPage(1);
          }}
          className="px-3 py-1.5 text-sm bg-dark-800 border border-dark-700 rounded-md text-white focus:outline-none focus:border-primary-500"
          title="Filtrer par compte bancaire"
        >
          <option value="all">Tous les comptes</option>
          {comptes.map((compte) => (
            <option key={compte.id} value={compte.id}>
              {compte.nom}
            </option>
          ))}
        </select>

        <div className="flex items-center space-x-2">
          {/* Sélecteurs personnalisés Mois / Année */}
          <div className="relative">
            <button
              onClick={() => {
                setShowMonthPicker(!showMonthPicker);
                setShowYearPicker(false);
              }}
              className="flex items-center space-x-2 px-2.5 py-1.5 text-sm bg-dark-800 border border-dark-700 rounded-md text-white hover:bg-dark-700 transition-colors"
              title="Choisir le mois"
            >
              <span>{selectedMonthIdx !== null ? MONTHS[selectedMonthIdx] : 'Mois'}</span>
              <span>▼</span>
            </button>
            {showMonthPicker && (
              <div className="absolute top-full left-0 mt-1 w-44 bg-dark-800 border border-dark-700 rounded-lg shadow-lg z-10">
                {MONTHS.map((m, idx) => (
                  <button
                    key={m}
                    onClick={() => {
                      const y = selectedYear || currentYear;
                      const monthStr = String(idx + 1).padStart(2, '0');
                      setFilterMonth(`${y}-${monthStr}`);
                      setShowMonthPicker(false);
                    }}
                    className="w-full px-3 py-2 text-left text-white hover:bg-dark-700 transition-colors"
                  >
                    {m}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative">
            <button
              onClick={() => {
                setShowYearPicker(!showYearPicker);
                setShowMonthPicker(false);
              }}
              className="flex items-center space-x-2 px-3 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white hover:bg-dark-700 transition-colors"
              title="Choisir l'année"
            >
              <span>{selectedYear || 'Année'}</span>
              <span>▼</span>
            </button>
            {showYearPicker && (
              <div className="absolute top-full left-0 mt-1 w-32 bg-dark-800 border border-dark-700 rounded-lg shadow-lg z-10 max-h-64 overflow-auto">
                {YEARS.map((y) => (
                  <button
                    key={y}
                    onClick={() => {
                      const monthIndex = selectedMonthIdx ?? new Date().getMonth();
                      const monthStr = String(monthIndex + 1).padStart(2, '0');
                      setFilterMonth(`${y}-${monthStr}`);
                      setShowYearPicker(false);
                    }}
                    className="w-full px-3 py-2 text-left text-white hover:bg-dark-700 transition-colors"
                  >
                    {y}
                  </button>
                ))}
              </div>
            )}
          </div>

          {filterMonth && (
            <button
              onClick={() => setFilterMonth('')}
              className="px-2.5 py-1.5 bg-dark-700 hover:bg-dark-600 text-white rounded-md transition-colors text-sm"
              title="Effacer le filtre par mois"
            >
              ×
            </button>
          )}

           <button
             onClick={() => {
               setPaginationEnabled(!paginationEnabled);
               setCurrentPage(1);
             }}
             className={`px-2.5 py-1.5 rounded-md transition-colors text-sm flex items-center space-x-2 ${
               paginationEnabled
                 ? 'bg-primary-500 text-white hover:bg-primary-600'
                 : 'bg-dark-700 text-white hover:bg-dark-600'
             }`}
             title={paginationEnabled ? 'Désactiver la pagination' : 'Activer la pagination'}
           >
             {paginationEnabled ? (
               <>
                 <Grid className="w-4 h-4" />
                 <span>Paginé</span>
               </>
             ) : (
               <>
                 <List className="w-4 h-4" />
                 <span>Tout</span>
               </>
             )}
           </button>

          <select
            value={groupBy}
            onChange={(e) => {
              setGroupBy(e.target.value as GroupByOption);
              setCollapsedGroups(new Set()); // Réinitialiser plié/déplié au changement de critère
            }}
            className="px-2.5 py-1.5 text-sm bg-dark-800 border border-dark-700 rounded-md text-white focus:outline-none focus:border-primary-500"
            title="Regrouper par"
          >
            <option value="none">Regrouper par : Aucun</option>
            <option value="month">Regrouper par : Mois</option>
            <option value="year">Regrouper par : Année</option>
            <option value="type">Regrouper par : Type</option>
          </select>
         </div>
      </div>

      {/* Liste des transactions (groupée ou plate) */}
      <div className="space-y-3">
        {filteredTransactions.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-dark-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Filter className="w-8 h-8 text-dark-400" />
            </div>
            <p className="text-white font-medium mb-2">
              {hasActiveFilters ? 'Aucune transaction trouvée' : 'Aucune transaction'}
            </p>
            <p className="text-dark-400">
              {hasActiveFilters
                ? 'Essayez de modifier vos filtres de recherche'
                : 'Commencez par ajouter votre première transaction'
              }
            </p>
            {hasActiveFilters && (
              <div className="mt-2 text-xs text-dark-400">
                <p>Filtres actifs :</p>
                {searchTerm && <p>• Recherche : "{searchTerm}"</p>}
                {filterType !== 'all' && <p>• Type : {transactionTypeLabel(filterType)}</p>}
                {filterCategory !== 'all' && (
                  <p>• Catégorie : {categories.find(c => c.id === filterCategory)?.nom || 'Inconnue'}</p>
                )}
                {filterCompte !== 'all' && (
                  <p>• Compte : {comptes.find(c => c.id === filterCompte)?.nom || 'Inconnu'}</p>
                )}
                {filterMonth && <p>• Mois : {new Date(filterMonth).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}</p>}
              </div>
            )}
          </div>
        ) : hasGrouping ? (
          groupedEntries().map((group) => {
            const isCollapsed = collapsedGroups.has(group.key);
            return (
            <div key={group.key} className="space-y-2">
              <button
                type="button"
                onClick={() => toggleGroup(group.key)}
                className="w-full flex items-center gap-2 text-left text-body-sm font-semibold text-dark-300 uppercase tracking-wider pt-2 first:pt-0 border-b border-dark-700 pb-1.5 hover:text-white hover:bg-dark-800/50 rounded-t transition-colors"
                aria-expanded={!isCollapsed}
              >
                {isCollapsed ? (
                  <ChevronRight className="w-4 h-4 shrink-0 text-dark-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 shrink-0 text-dark-400" />
                )}
                <span>{group.label}</span>
                <span className="text-dark-500 font-normal">({group.items.length})</span>
              </button>
              {!isCollapsed && group.items.map((transaction) => (
                <div
                  key={transaction.id}
                  className="flex items-center justify-between p-2.5 bg-dark-800 rounded-md hover:bg-dark-700 transition-colors"
                >
                  <div className="flex items-center space-x-3 min-w-0">
                    <CategoryIconBadge color={transaction.categorie?.couleur || '#666'} icon={transaction.categorie?.icone} size={32} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <h3 className="text-body text-white font-medium truncate">{transaction.description}</h3>
                        {transaction.attachment_path && (
                          <Paperclip className="w-3.5 h-3.5 text-primary-400 shrink-0" aria-hidden title="Pièce jointe" />
                        )}
                        {transaction.type === 'transfert' && (
                          <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-[11px] rounded shrink-0">
                            Transfert
                          </span>
                        )}
                        {transaction.type === 'pret' && (
                          <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-[11px] rounded shrink-0">
                            Prêt
                          </span>
                        )}
                        {transaction.type === 'remboursement' && (
                          <span className="px-1.5 py-0.5 bg-teal-500/20 text-teal-400 text-[11px] rounded shrink-0">
                            Remboursement
                          </span>
                        )}
                      </div>
                      <div className="flex items-center space-x-2 text-body-sm text-dark-400 mt-0.5 flex-wrap">
                        <span>{transaction.categorie?.nom}</span>
                        <span>•</span>
                        <span>{transaction.compte?.nom}</span>
                        {transaction.compte_destination && (
                          <>
                            <span>→</span>
                            <span>{transaction.compte_destination.nom}</span>
                          </>
                        )}
                        <span>•</span>
                        <span>{formatDate(transaction.date)}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 shrink-0 ml-2">
                    <div className="text-right">
                      <p className={`text-body font-semibold ${transaction.montant < 0 ? 'text-red-400' : 'text-green-400'}`}>
                        {transaction.montant < 0 ? '-' : '+'}{formatCurrency(transaction.montant)}
                      </p>
                      <p className="text-[11px] text-dark-400">
                        Type: {transaction.type || 'non défini'} | Montant: {transaction.montant}
                      </p>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleEdit(transaction)}
                        className="p-1.5 text-dark-400 hover:text-white hover:bg-dark-600 rounded-md transition-colors"
                        title="Modifier"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => transaction.id && handleDelete(transaction.id)}
                        className="p-1.5 text-dark-400 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            );
          })
        ) : (
          currentTransactions.map((transaction) => (
            <div
              key={transaction.id}
              className="flex items-center justify-between p-2.5 bg-dark-800 rounded-md hover:bg-dark-700 transition-colors"
            >
              <div className="flex items-center space-x-3 min-w-0">
                <CategoryIconBadge color={transaction.categorie?.couleur || '#666'} icon={transaction.categorie?.icone} size={32} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <h3 className="text-body text-white font-medium truncate">{transaction.description}</h3>
                    {transaction.attachment_path && (
                      <Paperclip className="w-3.5 h-3.5 text-primary-400 shrink-0" aria-hidden title="Pièce jointe" />
                    )}
                    {transaction.type === 'transfert' && (
                      <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 text-[11px] rounded shrink-0">
                        Transfert
                      </span>
                    )}
                    {transaction.type === 'pret' && (
                      <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 text-[11px] rounded shrink-0">
                        Prêt
                      </span>
                    )}
                    {transaction.type === 'remboursement' && (
                      <span className="px-1.5 py-0.5 bg-teal-500/20 text-teal-400 text-[11px] rounded shrink-0">
                        Remboursement
                      </span>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 text-body-sm text-dark-400 mt-0.5 flex-wrap">
                    <span>{transaction.categorie?.nom}</span>
                    <span>•</span>
                    <span>{transaction.compte?.nom}</span>
                    {transaction.compte_destination && (
                      <>
                        <span>→</span>
                        <span>{transaction.compte_destination.nom}</span>
                      </>
                    )}
                    <span>•</span>
                    <span>{formatDate(transaction.date)}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2 shrink-0 ml-2">
                <div className="text-right">
                  <p className={`text-body font-semibold ${transaction.montant < 0 ? 'text-red-400' : 'text-green-400'}`}>
                    {transaction.montant < 0 ? '-' : '+'}{formatCurrency(transaction.montant)}
                  </p>
                  <p className="text-[11px] text-dark-400">
                    Type: {transaction.type || 'non défini'} | Montant: {transaction.montant}
                  </p>
                </div>
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handleEdit(transaction)}
                    className="p-1.5 text-dark-400 hover:text-white hover:bg-dark-600 rounded-md transition-colors"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => transaction.id && handleDelete(transaction.id)}
                    className="p-1.5 text-dark-400 hover:text-red-400 hover:bg-red-500/10 rounded-md transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
       </div>

       {/* Contrôles de pagination (masquée en mode groupé) */}
       {!hasGrouping && paginationEnabled && totalPages > 1 && (
         <div className="flex items-center justify-center space-x-2 py-3">
           <button
             onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
             disabled={currentPage === 1}
             className="px-2.5 py-1.5 text-sm bg-dark-800 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-dark-700 transition-colors"
           >
             Précédent
           </button>
            <div className="flex items-center space-x-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNumber: number;
                if (totalPages <= 5) {
                  pageNumber = i + 1;
                } else if (currentPage <= 3) {
                  pageNumber = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNumber = totalPages - 4 + i;
                } else {
                  pageNumber = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNumber}
                    onClick={() => setCurrentPage(pageNumber)}
                    className={`px-2.5 py-1.5 text-sm rounded-md transition-colors ${
                      currentPage === pageNumber
                        ? 'bg-primary-500 text-white'
                        : 'bg-dark-800 text-white hover:bg-dark-700'
                    }`}
                  >
                    {pageNumber}
                  </button>
                );
              })}
            </div>
           <button
             onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
             disabled={currentPage === totalPages}
             className="px-2.5 py-1.5 text-sm bg-dark-800 text-white rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-dark-700 transition-colors"
           >
             Suivant
           </button>
         </div>
       )}

       {/* Modal du formulaire */}
      <TransactionForm
        transaction={editingTransaction}
        isOpen={isFormOpen}
        onClose={handleCloseForm}
        onSubmit={handleFormSubmit}
        onEditTransaction={onEditTransaction}
        title={editingTransaction ? 'Modifier la transaction' : 'Nouvelle transaction'}
      />
    </div>
  );
};

export default TransactionList; 